import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Icon,
  errorMessage,
  useApi,
  useMe,
  useRealtimeMessages,
  useToast,
} from "@saas/ui";
import type { ChatCallSummary } from "../../api/chat.dto.js";
import { chatApi, chatKeys } from "../api.js";
import { openCallPage, openCallWindow } from "../call-window.js";
import {
  startCallRingtone,
  stopCallRingtone,
  unlockCallAudio,
} from "../call-ringtone.js";

interface RingClaim {
  meetingId: string;
  tabId: string;
  visible: boolean;
  at: number;
}

type CallChannelMessage =
  ({ type: "claim" } & RingClaim) | { type: "handled"; meetingId: string };

interface ChatCallRootValue {
  present(call: ChatCallSummary): void;
  isHandled(meetingId: string): boolean;
  markAnswered(meetingId: string): void;
  startDialing(): void;
  registerOutgoing(meetingId: string): void;
  stopDialing(): void;
}

const NO_CALL_ROOT: ChatCallRootValue = {
  present: () => undefined,
  isHandled: () => false,
  markAnswered: () => undefined,
  startDialing: () => undefined,
  registerOutgoing: () => undefined,
  stopDialing: () => undefined,
};

const ChatCallRootContext = createContext<ChatCallRootValue>(NO_CALL_ROOT);

function tabId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `call-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`
  );
}

function electRinger(claims: readonly RingClaim[]): string | null {
  const sorted = [...claims].sort((left, right) => {
    if (left.visible !== right.visible) return left.visible ? -1 : 1;
    if (left.at !== right.at) return left.at - right.at;
    return left.tabId.localeCompare(right.tabId);
  });
  return sorted[0]?.tabId ?? null;
}

function realtimeCall(payload: unknown): ChatCallSummary | null {
  if (typeof payload !== "object" || payload === null) return null;
  const value = payload as Record<string, unknown>;
  if (
    typeof value.meetingId !== "string" ||
    typeof value.conversationId !== "string" ||
    (value.kind !== "audio" && value.kind !== "video") ||
    typeof value.startedBy !== "string"
  ) {
    return null;
  }
  return {
    meetingId: value.meetingId,
    conversationId: value.conversationId,
    kind: value.kind,
    state: value.state === "running" ? "running" : "scheduled",
    startedBy: value.startedBy,
    startedByName:
      typeof value.startedByName === "string" && value.startedByName.trim()
        ? value.startedByName.trim()
        : "参加者",
    createdAt: new Date(
      typeof value.createdAt === "string" ? value.createdAt : Date.now(),
    ),
    canEnd: false,
  };
}

/**
 * ルート遷移の外で着信を保持する。チャット以外の画面へ移動しても着信表示と音が消えない。
 * 同じアカウントで複数タブを開いている場合は、表示中の1タブだけが応答画面を出す。
 */
export function ChatCallRoot({ children }: { children: ReactNode }) {
  const api = useApi();
  const me = useMe();
  const toast = useToast();
  const qc = useQueryClient();
  const ownTabId = useRef(tabId());
  const channel = useRef<BroadcastChannel | null>(null);
  const claims = useRef(new Map<string, RingClaim[]>());
  const elections = useRef(new Map<string, number>());
  const handled = useRef(new Set<string>());
  const answeredByOthers = useRef(new Set<string>());
  const outgoingMeetingId = useRef<string | null>(null);
  const outgoingTimer = useRef<number | null>(null);
  const [handledVersion, setHandledVersion] = useState(0);
  const [incoming, setIncoming] = useState<ChatCallSummary | null>(null);
  const [joining, setJoining] = useState(false);
  const [declining, setDeclining] = useState(false);

  const recordClaim = useCallback((claim: RingClaim): void => {
    const list = claims.current.get(claim.meetingId) ?? [];
    if (!list.some((item) => item.tabId === claim.tabId)) list.push(claim);
    claims.current.set(claim.meetingId, list);
  }, []);

  const handle = useCallback((meetingId: string, broadcast: boolean): void => {
    handled.current.add(meetingId);
    setHandledVersion((version) => version + 1);
    setIncoming((current) =>
      current?.meetingId === meetingId ? null : current,
    );
    stopCallRingtone();
    const timer = elections.current.get(meetingId);
    if (timer !== undefined) window.clearTimeout(timer);
    elections.current.delete(meetingId);
    claims.current.delete(meetingId);
    if (broadcast)
      channel.current?.postMessage({
        type: "handled",
        meetingId,
      } satisfies CallChannelMessage);
  }, []);

  const stopDialing = useCallback((): void => {
    outgoingMeetingId.current = null;
    if (outgoingTimer.current !== null)
      window.clearTimeout(outgoingTimer.current);
    outgoingTimer.current = null;
    stopCallRingtone();
  }, []);

  const startDialing = useCallback((): void => {
    stopDialing();
    unlockCallAudio();
    startCallRingtone("outgoing");
    outgoingTimer.current = window.setTimeout(stopDialing, 120_000);
  }, [stopDialing]);

  const registerOutgoing = useCallback(
    (meetingId: string): void => {
      if (
        handled.current.has(meetingId) ||
        answeredByOthers.current.has(meetingId)
      ) {
        stopDialing();
        return;
      }
      outgoingMeetingId.current = meetingId;
    },
    [stopDialing],
  );

  const present = useCallback(
    (call: ChatCallSummary): void => {
      if (
        call.startedBy === me.user.id ||
        handled.current.has(call.meetingId) ||
        elections.current.has(call.meetingId)
      )
        return;
      const mine: RingClaim = {
        meetingId: call.meetingId,
        tabId: ownTabId.current,
        visible: document.visibilityState === "visible",
        at: Date.now(),
      };
      recordClaim(mine);
      if (!channel.current) {
        setIncoming((current) => current ?? call);
        return;
      }
      channel.current.postMessage({
        type: "claim",
        ...mine,
      } satisfies CallChannelMessage);
      const timer = window.setTimeout(() => {
        elections.current.delete(call.meetingId);
        if (handled.current.has(call.meetingId)) return;
        const winner = electRinger(
          claims.current.get(call.meetingId) ?? [mine],
        );
        claims.current.delete(call.meetingId);
        if (winner === ownTabId.current)
          setIncoming((current) => current ?? call);
      }, 180);
      elections.current.set(call.meetingId, timer);
    },
    [me.user.id, recordClaim],
  );

  useEffect(() => {
    const name = `saas-chat-call-${me.tenant.id}-${me.user.id}`;
    if (typeof BroadcastChannel === "undefined") return;
    const opened = new BroadcastChannel(name);
    channel.current = opened;
    opened.onmessage = (event: MessageEvent<unknown>) => {
      if (typeof event.data !== "object" || event.data === null) return;
      const message = event.data as Partial<CallChannelMessage>;
      if (
        message.type === "claim" &&
        typeof message.meetingId === "string" &&
        typeof message.tabId === "string" &&
        typeof message.visible === "boolean" &&
        typeof message.at === "number"
      ) {
        recordClaim(message as RingClaim);
      } else if (
        message.type === "handled" &&
        typeof message.meetingId === "string"
      ) {
        handle(message.meetingId, false);
      }
    };
    return () => {
      opened.close();
      if (channel.current === opened) channel.current = null;
    };
  }, [handle, me.tenant.id, me.user.id, recordClaim]);

  useEffect(() => {
    const unlock = (): void => unlockCallAudio();
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  useEffect(
    () => () => {
      stopDialing();
      for (const timer of elections.current.values())
        window.clearTimeout(timer);
      elections.current.clear();
    },
    [stopDialing],
  );

  useRealtimeMessages(["telephony"], (event) => {
    if (event.type === "chat.call.started") {
      const call = realtimeCall(event.payload);
      if (call) present(call);
      return;
    }
    if (typeof event.payload !== "object" || event.payload === null) return;
    const payload = event.payload as {
      meetingId?: unknown;
      answeredBy?: unknown;
      declinedBy?: unknown;
      declinedByName?: unknown;
      ended?: unknown;
    };
    if (typeof payload.meetingId !== "string") return;
    if (event.type === "chat.call.ended") {
      handle(payload.meetingId, false);
      if (outgoingMeetingId.current === payload.meetingId) stopDialing();
    }
    if (event.type === "chat.call.answered") {
      if (payload.answeredBy === me.user.id) handle(payload.meetingId, false);
      if (payload.answeredBy !== me.user.id) {
        answeredByOthers.current.add(payload.meetingId);
        if (outgoingMeetingId.current === payload.meetingId) stopDialing();
      }
    }
    if (event.type === "chat.call.declined") {
      const declinedByMe = payload.declinedBy === me.user.id;
      const ended = payload.ended === true;
      if (declinedByMe || ended) handle(payload.meetingId, false);
      if (outgoingMeetingId.current === payload.meetingId) {
        const name =
          typeof payload.declinedByName === "string" && payload.declinedByName.trim()
            ? payload.declinedByName.trim()
            : "相手";
        toast.error(`${name}さんが通話を拒否しました`);
        if (ended) stopDialing();
      }
    }
  });

  useEffect(() => {
    if (!incoming) return;
    startCallRingtone();
    const previousTitle = document.title;
    document.title = `着信中 · ${incoming.startedByName}`;
    const createdAt = new Date(incoming.createdAt).getTime();
    const remaining = Number.isFinite(createdAt)
      ? Math.max(1_000, 120_000 - (Date.now() - createdAt))
      : 120_000;
    const timeout = window.setTimeout(
      () => handle(incoming.meetingId, true),
      remaining,
    );
    return () => {
      window.clearTimeout(timeout);
      stopCallRingtone();
      if (document.title.startsWith("着信中 · "))
        document.title = previousTitle;
    };
  }, [handle, incoming]);

  const answer = (): void => {
    if (!incoming || joining) return;
    unlockCallAudio();
    const target = openCallWindow();
    if (!target) {
      toast.error(
        "通話画面を開けませんでした。ブラウザでポップアップを許可してください",
      );
      return;
    }
    const call = incoming;
    setJoining(true);
    stopCallRingtone();
    handle(call.meetingId, true);
    void qc.invalidateQueries({ queryKey: chatKeys.call(call.conversationId) });
    openCallPage(target, call.conversationId, call.meetingId);
    setJoining(false);
  };

  const decline = (): void => {
    if (!incoming || joining || declining) return;
    const call = incoming;
    setDeclining(true);
    stopCallRingtone();
    void chatApi
      .declineCall(api, call.conversationId, call.meetingId)
      .then(() => {
        handle(call.meetingId, true);
        void qc.invalidateQueries({ queryKey: chatKeys.call(call.conversationId) });
      })
      .catch((error) => {
        toast.error(errorMessage(error));
        startCallRingtone();
      })
      .finally(() => setDeclining(false));
  };

  const value = useMemo<ChatCallRootValue>(
    () => ({
      present,
      isHandled: (meetingId) => handled.current.has(meetingId),
      markAnswered: (meetingId) => handle(meetingId, true),
      startDialing,
      registerOutgoing,
      stopDialing,
    }),
    [
      handle,
      handledVersion,
      present,
      registerOutgoing,
      startDialing,
      stopDialing,
    ],
  );

  return (
    <ChatCallRootContext.Provider value={value}>
      {children}
      {incoming ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/55 p-4"
          role="presentation"
        >
          <section
            className="flex w-full max-w-sm flex-col items-center gap-4 rounded-card border border-line bg-card p-6 text-center shadow-float"
            role="dialog"
            aria-modal="true"
            aria-label={`${incoming.startedByName}さんから着信`}
          >
            <span
              className="inline-flex h-16 w-16 animate-pulse items-center justify-center rounded-pill bg-brand text-3xl text-brand-on"
              aria-hidden="true"
            >
              <Icon name={incoming.kind === "video" ? "videocam" : "call"} size={28} />
            </span>
            <div>
              <p className="text-callout font-medium text-fg-secondary">
                {incoming.kind === "video"
                  ? "ビデオ通話の着信"
                  : "音声通話の着信"}
              </p>
              <p className="mt-1 text-title wrap-name">
                {incoming.startedByName}
              </p>
            </div>
            <div className="grid w-full grid-cols-2 gap-3">
              <Button
                variant="danger"
                size="md"
                onClick={decline}
                loading={declining}
                disabled={joining}
              >
                拒否する
              </Button>
              <Button
                variant="primary"
                size="md"
                icon={incoming.kind === "video" ? "videocam" : "call"}
                onClick={answer}
                loading={joining}
                disabled={declining}
              >
                応答する
              </Button>
            </div>
          </section>
        </div>
      ) : null}
    </ChatCallRootContext.Provider>
  );
}

export function useChatCallRoot(): ChatCallRootValue {
  return useContext(ChatCallRootContext);
}
